import { getDefaultYAxisTitle, makeFormatter, makePercentFormatter } from "./reportingUtils";
import { exportFileName } from "../../utils/tableExport";

// Adding a new widget type = adding an entry here
// (and, for a non-Apex kind, a render branch in ReportWidget).
export const WIDGET_TYPES = {
  line: { label: "Line chart", kind: "chart", apexType: "line" },
  area: { label: "Area chart", kind: "chart", apexType: "area" },
  bar: { label: "Bar chart", kind: "chart", apexType: "bar" },
  stackedBar: { label: "Stacked bar chart", kind: "chart", apexType: "bar", stacked: true },
  table: { label: "Table", kind: "table" },
};

export const getWidgetType = (type) => WIDGET_TYPES[type] ?? WIDGET_TYPES.line;

export const WIDGET_TYPE_OPTIONS = Object.entries(WIDGET_TYPES).map(([value, def]) => ({
  value,
  label: def.label,
}));

// series: what the chart shows (Δ series carry isDiff); compare: from getChartCompare.
export function buildChartOptions(widget, { periodCats, fmt, series = [], compare = null }) {
  const def = getWidgetType(widget.type);
  const { settings } = widget;
  const isPercent = compare?.mode === "pct";
  const formatValue = isPercent ? makePercentFormatter(fmt) : makeFormatter(fmt);
  const defaultYTitle = isPercent ? "Change (%)" : compare?.mode === "diff" ? "Difference" : getDefaultYAxisTitle(fmt);
  const showValues = !!settings.dataLabels;
  const isBar = def.apexType === "bar";
  const filename = exportFileName(settings.title || def.label);

  return {
    chart: {
      type: def.apexType,
      stacked: !!def.stacked,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: { png: { filename }, svg: { filename }, csv: { filename } },
      },
    },
    // crosshairs: no shaded band behind the hovered column
    xaxis: { type: "category", categories: periodCats.map((c) => c.label), crosshairs: { show: false } },
    yaxis: {
      title: {
        text: settings.yTitle?.trim() || defaultYTitle,
        style: { fontSize: "12px", fontWeight: 600 },
      },
      labels: { formatter: formatValue, style: { fontSize: "12px" } },
    },
    // Light tooltip; never filled with the series color
    tooltip: { theme: "light", fillSeriesColor: false, y: { formatter: formatValue } },
    dataLabels: { enabled: showValues, formatter: (val) => formatValue(val) },
    // highlightDataSeries: hovering a legend item doesn't fade the other series
    legend: { position: "bottom", onItemHover: { highlightDataSeries: false } },
    // Keep series colors unchanged on hover and click
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
    noData: { text: "Add a series to show data" },
    ...(isBar
      ? {
          plotOptions: {
            bar: {
              borderRadius: 6,
              borderRadiusApplication: "end",
              borderRadiusWhenStacked: "last",
              dataLabels: def.stacked
                ? {
                    total: {
                      enabled: showValues,
                      formatter: (val) => formatValue(val),
                      style: { fontSize: "12px", fontWeight: 700 },
                    },
                  }
                : {},
            },
          },
          fill: { opacity: 1 },
        }
      : {
          // Δ lines dashed when shown next to the values
          stroke: { curve: "straight", width: 2, dashArray: series.map((s) => (s.isDiff ? 5 : 0)) },
          // Striped rows only for line charts; they hide an area chart's fill
          ...(def.apexType === "line"
            ? { grid: { row: { colors: ["#f3f3f3", "transparent"], opacity: 0.5 } } }
            : {}),
          // Area: clearly visible solid fill under the line
          fill: def.apexType === "area" ? { type: "solid", opacity: 0.35 } : { opacity: 1 },
        }),
  };
}